/**
 * @file DashboardDrillDownView.js
 * @description Drill-down view for Most Visited Pages and Most Clicked Content dashboards.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
    DashboardTypeEnum,
    EventDataTypeEnum,
    LogEventType,
} from '../../utils/data/enums';
import NotEnoughData from './NotEnoughData';
import Spinner from '../ui/spinners/Spinner';
import {
    FilterModal,
    renderFilterIcon,
    useFilter,
} from '../helpers/FilterComponent';
import PaginationComponent from '../helpers/PaginationComponent';
import './DashboardDrillDownView.scss';
import { getText } from '../../i18n';

export const DRILL_DOWN_TYPE_LABELS = Object.freeze({
    [DashboardTypeEnum.MostVisitedPages]: {
        title: getText('msg_d71987f66387'),
        subtitle: getText('msg_e01cd31b8d62'),
    },
    [DashboardTypeEnum.MostClickedContent]: {
        title: getText('msg_435f44a4684b'),
        subtitle: getText('msg_e01cd31b8d62'),
    },
});

export function getDrillDownLabels(dashboardTypeId) {
    return (
        DRILL_DOWN_TYPE_LABELS[dashboardTypeId] ||
        DRILL_DOWN_TYPE_LABELS[DashboardTypeEnum.MostVisitedPages]
    );
}

const CONTENT_TYPE_LABELS = Object.freeze({
    [LogEventType.UserVideoPlayed]: 'Video',
    [LogEventType.UserDocumentViewed]: 'Document',
    [LogEventType.UserLinkClicked]: 'Link',
});

const LOCATION_TYPE_LABELS = Object.freeze(
    Object.fromEntries(
        Object.entries(EventDataTypeEnum).map(function mapLocationTypeEntry([
            label,
            id,
        ]) {
            return [id, label];
        }),
    ),
);

function toNumber(value) {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
        return 0;
    }

    return parsed;
}

function resolveContentTypeLabel(eventTypeId) {
    return CONTENT_TYPE_LABELS[eventTypeId] || 'Unknown';
}

function resolveLocationTypeLabel(locationType) {
    return LOCATION_TYPE_LABELS[locationType] || 'Unknown';
}

function normalizeMostVisitedPagesRows(data) {
    const safeRows = Array.isArray(data) ? data : [];

    return safeRows
        .map(function mapMostVisitedRow(row) {
            return {
                name: row?.name || 'Unknown',
                views: toNumber(row?.count),
                uniqueVisitors: toNumber(row?.uniqueViewers),
                averageTime: row?.averageTime || '-',
            };
        })
        .sort(function sortMostVisitedRows(first, second) {
            return second.views - first.views;
        });
}

function normalizeMostClickedContentRows(data) {
    const safeRows = Array.isArray(data) ? data : [];

    return safeRows
        .map(function mapMostClickedRow(row) {
            const type = resolveContentTypeLabel(row?.eventTypeId);
            return {
                name: row?.name || 'Unknown',
                type,
                location: resolveLocationTypeLabel(row?.locationType),
                page: row?.locationName || '-',
                clicks: toNumber(row?.count),
                uniqueClicks: toNumber(row?.uniqueClicks),
            };
        })
        .sort(function sortMostClickedRows(first, second) {
            return second.clicks - first.clicks;
        });
}

function formatLargeNumber(value) {
    return toNumber(value).toLocaleString('en-US');
}

const FILTER_MODAL_WIDTH = 253;
const FILTER_MODAL_GUTTER = 12;

function buildViewportModalStyle(triggerElement, alignRight = false) {
    if (!triggerElement) {
        return null;
    }

    const triggerRect = triggerElement.getBoundingClientRect();
    const maxLeft = Math.max(
        FILTER_MODAL_GUTTER,
        window.innerWidth - FILTER_MODAL_WIDTH - FILTER_MODAL_GUTTER,
    );
    const alignedLeft = alignRight
        ? triggerRect.right - FILTER_MODAL_WIDTH
        : triggerRect.left;
    const clampedLeft = Math.min(
        maxLeft,
        Math.max(FILTER_MODAL_GUTTER, alignedLeft),
    );

    return {
        position: 'fixed',
        top: `${triggerRect.bottom + 4}px`,
        left: `${clampedLeft}px`,
        zIndex: 1000,
        marginTop: 0,
    };
}

function isScrollWithinFilterModal(eventTarget) {
    return (
        eventTarget &&
        typeof eventTarget.closest === 'function' &&
        Boolean(eventTarget.closest('.filterModal'))
    );
}

function getFilterOptions(rows, selections, column) {
    const filtersWithoutCurrent = { ...selections };
    delete filtersWithoutCurrent[column];

    return Array.from(
        new Set(
            rows
                .filter(function filterRows(row) {
                    return Object.entries(filtersWithoutCurrent).every(
                        function hasMatchingSelection([key, values]) {
                            return (
                                !Array.isArray(values) ||
                                values.length === 0 ||
                                values.includes(row[key])
                            );
                        },
                    );
                })
                .map(function mapRowToOption(row) {
                    return row[column];
                }),
        ),
    )
        .filter(Boolean)
        .sort(function sortOptions(first, second) {
            return String(first).localeCompare(String(second));
        });
}

const MOST_VISITED_PAGES_PAGE_SIZE = 20;
const DRILL_DOWN_SPINNER_WIDTH = 50;
const SORT_DIRECTIONS = Object.freeze({
    ASCENDING: 'asc',
    DESCENDING: 'desc',
});

function getSortArrowIcon(isActive, isDescending) {
    return (
        <svg
            width={11}
            height={6}
            viewBox="0 0 11 6"
            fill="none"
            style={{
                transform: isActive && isDescending ? 'rotate(180deg)' : 'none',
                marginLeft: '4px',
                display: 'inline-block',
            }}
        >
            <path
                d="M1 0.75L5.25 5.25L9.5 0.75"
                stroke="#2F2B43"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function MostVisitedPagesDrillDown({
    data,
    onQueryChange,
    dashboardTypeId,
    isLoading,
    onRowsForExportChange,
}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({
        key: 'views',
        direction: SORT_DIRECTIONS.DESCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [activeModalStyle, setActiveModalStyle] = useState(null);
    const [selections, setSelections] = useState({});

    const rows = useMemo(
        function buildMostVisitedRows() {
            return normalizeMostVisitedPagesRows(data);
        },
        [data],
    );
    const [filteredRows, applyFilter] = useFilter(rows);

    const totalViews = useMemo(
        function getMostVisitedTotal() {
            return filteredRows.reduce(function sumViews(sum, row) {
                return sum + row.views;
            }, 0);
        },
        [filteredRows],
    );

    const sortedRows = useMemo(
        function sortMostVisitedRows() {
            const sorted = [...filteredRows];
            sorted.sort(function compareRows(a, b) {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'averageTime') {
                    aValue = typeof aValue === 'string' ? 0 : aValue;
                    bValue = typeof bValue === 'string' ? 0 : bValue;
                }

                if (typeof aValue === 'string') {
                    return sortConfig.direction === SORT_DIRECTIONS.ASCENDING
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                if (sortConfig.direction === SORT_DIRECTIONS.ASCENDING) {
                    return aValue - bValue;
                }
                return bValue - aValue;
            });

            return sorted;
        },
        [filteredRows, sortConfig],
    );

    const paginatedRows = useMemo(
        function getPaginatedRows() {
            const startIndex = (currentPage - 1) * MOST_VISITED_PAGES_PAGE_SIZE;
            const endIndex = startIndex + MOST_VISITED_PAGES_PAGE_SIZE;
            return sortedRows.slice(startIndex, endIndex);
        },
        [sortedRows, currentPage],
    );

    const totalPages = Math.max(
        1,
        Math.ceil(sortedRows.length / MOST_VISITED_PAGES_PAGE_SIZE),
    );

    useEffect(
        function syncRowsForExport() {
            if (!onRowsForExportChange) {
                return;
            }

            onRowsForExportChange(
                sortedRows.map(function mapRowForExport(row) {
                    return {
                        name: row.name,
                        views: row.views,
                        uniqueVisitors: row.uniqueVisitors,
                    };
                }),
            );
        },
        [onRowsForExportChange, sortedRows],
    );

    useEffect(
        function syncCurrentPageToAvailableResults() {
            if (currentPage > totalPages) {
                setCurrentPage(totalPages);
            }
        },
        [currentPage, totalPages],
    );

    useEffect(
        function closeModalOnViewportChange() {
            if (!activeModal) {
                return undefined;
            }

            const handleViewportChange = function handleViewportChange(event) {
                if (isScrollWithinFilterModal(event?.target)) {
                    return;
                }

                setActiveModal(null);
                setActiveModalStyle(null);
            };

            window.addEventListener('resize', handleViewportChange);
            window.addEventListener('scroll', handleViewportChange, true);

            return function cleanupViewportListeners() {
                window.removeEventListener('resize', handleViewportChange);
                window.removeEventListener(
                    'scroll',
                    handleViewportChange,
                    true,
                );
            };
        },
        [activeModal],
    );

    const openModal = function openFilterModal(
        column,
        event,
        alignRight = false,
    ) {
        setActiveModalStyle(
            buildViewportModalStyle(event.currentTarget, alignRight),
        );
        setActiveModal(function toggleCurrentModal(currentColumn) {
            return currentColumn === column ? null : column;
        });
    };

    const closeModal = function closeFilterModal() {
        setActiveModal(null);
        setActiveModalStyle(null);
    };

    const handleApply = function handleApplyFilter(column, selectedOptions) {
        const nextSelections = {
            ...selections,
            [column]: selectedOptions,
        };

        setSelections(nextSelections);
        applyFilter(column, selectedOptions);
        setCurrentPage(1);

        if (onQueryChange) {
            onQueryChange(dashboardTypeId, {
                page: 1,
                pageSize: MOST_VISITED_PAGES_PAGE_SIZE,
                sortBy: sortConfig.key,
                sortOrder: sortConfig.direction,
                filters: nextSelections,
            });
        }
    };

    const handleSortClick = function handleSortColumn(columnName) {
        const newSortOrder =
            sortConfig.key === columnName &&
            sortConfig.direction === SORT_DIRECTIONS.ASCENDING
                ? SORT_DIRECTIONS.DESCENDING
                : SORT_DIRECTIONS.ASCENDING;

        setSortConfig({
            key: columnName,
            direction: newSortOrder,
        });
        setCurrentPage(1);

        if (onQueryChange) {
            onQueryChange(dashboardTypeId, {
                page: 1,
                pageSize: MOST_VISITED_PAGES_PAGE_SIZE,
                sortBy: columnName,
                sortOrder: newSortOrder,
                filters: selections,
            });
        }
    };

    const handlePageChange = function handlePaginationChange(newPage) {
        setCurrentPage(newPage);

        if (onQueryChange) {
            onQueryChange(dashboardTypeId, {
                page: newPage,
                pageSize: MOST_VISITED_PAGES_PAGE_SIZE,
                sortBy: sortConfig.key,
                sortOrder: sortConfig.direction,
                filters: selections,
            });
        }
    };

    if (isLoading) {
        return (
            <div className="dashboard-drill-down-content">
                <Spinner
                    width={DRILL_DOWN_SPINNER_WIDTH}
                    className="dashboard-loading-spinner"
                />
            </div>
        );
    }

    if (rows.length === 0) {
        return <NotEnoughData />;
    }

    return (
        <div className="dashboard-drill-down-content">
            <div className="dashboard-drill-down-table-shell">
                <div className="dashboard-drill-down-table-meta">
                    <div className="dashboard-drill-down-total">
                        {formatLargeNumber(totalViews)}
                    </div>
                    <div className="dashboard-drill-down-total-label">
                        {getText('msg_cb86c1295145')}
                    </div>
                </div>
                <div className="dashboard-drill-down-table-scroll">
                    <table className="dashboard-drill-down-table">
                        <thead>
                            <tr>
                                <th className="dashboard-drill-down-filter-header">
                                    <div className="dashboard-drill-down-header-control-group">
                                        <span>
                                            {getText('msg_709a23220f2c')}
                                        </span>
                                        <button
                                            className="dashboard-drill-down-filter-button"
                                            onClick={event =>
                                                openModal('name', event)
                                            }
                                            type="button"
                                        >
                                            {renderFilterIcon(
                                                selections.name?.length > 0,
                                            )}
                                        </button>
                                        {activeModal === 'name' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getFilterOptions(
                                                    rows,
                                                    selections,
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                modalStyle={activeModalStyle}
                                            />
                                        )}
                                    </div>
                                </th>
                                <th className="dashboard-drill-down-sortable-header">
                                    <button
                                        className="dashboard-drill-down-sortable-header-control"
                                        onClick={() => handleSortClick('views')}
                                        type="button"
                                    >
                                        {getText('msg_24be61285e09')}
                                        {getSortArrowIcon(
                                            sortConfig.key === 'views',
                                            sortConfig.direction ===
                                                SORT_DIRECTIONS.DESCENDING,
                                        )}
                                    </button>
                                </th>
                                <th className="dashboard-drill-down-sortable-header">
                                    <button
                                        className="dashboard-drill-down-sortable-header-control"
                                        onClick={() =>
                                            handleSortClick('uniqueVisitors')
                                        }
                                        type="button"
                                    >
                                        {getText('msg_2c483699fa6d')}
                                        {getSortArrowIcon(
                                            sortConfig.key === 'uniqueVisitors',
                                            sortConfig.direction ===
                                                SORT_DIRECTIONS.DESCENDING,
                                        )}
                                    </button>
                                </th>
                                {/* <th className="dashboard-drill-down-sortable-header">
                                    <button
                                        className="dashboard-drill-down-sortable-header-control"
                                        onClick={() =>
                                            handleSortClick('averageTime')
                                        }
                                        type="button"
                                    >
                                        Average Time
                                        {getSortArrowIcon(
                                            sortConfig.key === 'averageTime',
                                            sortConfig.direction ===
                                                SORT_DIRECTIONS.DESCENDING,
                                        )}
                                    </button>
                                </th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedRows.length === 0 && (
                                <tr>
                                    <td
                                        className="dashboard-drill-down-empty-state"
                                        colSpan={3}
                                    >
                                        {getText('msg_636400de23cd')}
                                    </td>
                                </tr>
                            )}
                            {paginatedRows.map(
                                function renderMostVisitedRow(row) {
                                    return (
                                        <tr key={row.name}>
                                            <td>{row.name}</td>
                                            <td className="dashboard-drill-down-metric-cell">
                                                {formatLargeNumber(row.views)}
                                            </td>
                                            <td className="dashboard-drill-down-metric-cell-unique-visitors">
                                                {row.uniqueVisitors > 0
                                                    ? formatLargeNumber(
                                                          row.uniqueVisitors,
                                                      )
                                                    : '-'}
                                            </td>
                                            {/* <td>{row.averageTime}</td> */}
                                        </tr>
                                    );
                                },
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="dashboard-drill-down-pagination">
                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
        </div>
    );
}

const MOST_CLICKED_CONTENT_PAGE_SIZE = 20;

function MostClickedContentDrillDown({
    data,
    onQueryChange,
    dashboardTypeId,
    isLoading,
    onRowsForExportChange,
}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({
        key: 'clicks',
        direction: SORT_DIRECTIONS.DESCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [activeModalStyle, setActiveModalStyle] = useState(null);
    const [selections, setSelections] = useState({});

    const rows = useMemo(
        function buildMostClickedRows() {
            return normalizeMostClickedContentRows(data);
        },
        [data],
    );
    const [filteredRows, applyFilter] = useFilter(rows);
    const totalClicks = useMemo(
        function getMostClickedTotal() {
            return filteredRows.reduce(function sumClicks(sum, row) {
                return sum + row.clicks;
            }, 0);
        },
        [filteredRows],
    );

    const sortedRows = useMemo(
        function sortMostClickedRows() {
            const sorted = [...filteredRows];
            sorted.sort(function compareRows(a, b) {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (typeof aValue === 'string') {
                    return sortConfig.direction === SORT_DIRECTIONS.ASCENDING
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                if (sortConfig.direction === SORT_DIRECTIONS.ASCENDING) {
                    return aValue - bValue;
                }
                return bValue - aValue;
            });

            return sorted;
        },
        [filteredRows, sortConfig],
    );

    const paginatedRows = useMemo(
        function getPaginatedRows() {
            const startIndex =
                (currentPage - 1) * MOST_CLICKED_CONTENT_PAGE_SIZE;
            const endIndex = startIndex + MOST_CLICKED_CONTENT_PAGE_SIZE;
            return sortedRows.slice(startIndex, endIndex);
        },
        [sortedRows, currentPage],
    );

    const totalPages = Math.max(
        1,
        Math.ceil(sortedRows.length / MOST_CLICKED_CONTENT_PAGE_SIZE),
    );

    useEffect(
        function syncRowsForExport() {
            if (!onRowsForExportChange) {
                return;
            }

            onRowsForExportChange(
                sortedRows.map(function mapRowForExport(row) {
                    return {
                        title: row.name,
                        type: row.type,
                        location: row.location,
                        page: row.page,
                        clicks: row.clicks,
                        uniqueClicks: row.uniqueClicks,
                    };
                }),
            );
        },
        [onRowsForExportChange, sortedRows],
    );

    useEffect(
        function syncCurrentPageToAvailableResults() {
            if (currentPage > totalPages) {
                setCurrentPage(totalPages);
            }
        },
        [currentPage, totalPages],
    );

    useEffect(
        function closeModalOnViewportChange() {
            if (!activeModal) {
                return undefined;
            }

            const handleViewportChange = function handleViewportChange(event) {
                if (isScrollWithinFilterModal(event?.target)) {
                    return;
                }

                setActiveModal(null);
                setActiveModalStyle(null);
            };

            window.addEventListener('resize', handleViewportChange);
            window.addEventListener('scroll', handleViewportChange, true);

            return function cleanupViewportListeners() {
                window.removeEventListener('resize', handleViewportChange);
                window.removeEventListener(
                    'scroll',
                    handleViewportChange,
                    true,
                );
            };
        },
        [activeModal],
    );

    const openModal = function openFilterModal(
        column,
        event,
        alignRight = false,
    ) {
        setActiveModalStyle(
            buildViewportModalStyle(event.currentTarget, alignRight),
        );
        setActiveModal(function toggleCurrentModal(currentColumn) {
            return currentColumn === column ? null : column;
        });
    };

    const closeModal = function closeFilterModal() {
        setActiveModal(null);
        setActiveModalStyle(null);
    };

    const handleApply = function handleApplyFilter(column, selectedOptions) {
        const nextSelections = {
            ...selections,
            [column]: selectedOptions,
        };

        setSelections(nextSelections);
        applyFilter(column, selectedOptions);
        setCurrentPage(1);

        if (onQueryChange) {
            onQueryChange(dashboardTypeId, {
                page: 1,
                pageSize: MOST_CLICKED_CONTENT_PAGE_SIZE,
                sortBy: sortConfig.key,
                sortOrder: sortConfig.direction,
                filters: nextSelections,
            });
        }
    };

    const handleSortClick = function handleSortColumn(columnName) {
        const newSortOrder =
            sortConfig.key === columnName &&
            sortConfig.direction === SORT_DIRECTIONS.ASCENDING
                ? SORT_DIRECTIONS.DESCENDING
                : SORT_DIRECTIONS.ASCENDING;

        setSortConfig({
            key: columnName,
            direction: newSortOrder,
        });
        setCurrentPage(1);

        if (onQueryChange) {
            onQueryChange(dashboardTypeId, {
                page: 1,
                pageSize: MOST_CLICKED_CONTENT_PAGE_SIZE,
                sortBy: columnName,
                sortOrder: newSortOrder,
                filters: selections,
            });
        }
    };

    const handlePageChange = function handlePaginationChange(newPage) {
        setCurrentPage(newPage);

        if (onQueryChange) {
            onQueryChange(dashboardTypeId, {
                page: newPage,
                pageSize: MOST_CLICKED_CONTENT_PAGE_SIZE,
                sortBy: sortConfig.key,
                sortOrder: sortConfig.direction,
                filters: selections,
            });
        }
    };

    if (isLoading) {
        return (
            <div className="dashboard-drill-down-content">
                <Spinner
                    width={DRILL_DOWN_SPINNER_WIDTH}
                    className="dashboard-loading-spinner"
                />
            </div>
        );
    }

    if (rows.length === 0) {
        return <NotEnoughData />;
    }

    return (
        <div className="dashboard-drill-down-content">
            <div className="dashboard-drill-down-table-shell">
                <div className="dashboard-drill-down-table-meta">
                    <div className="dashboard-drill-down-total">
                        {formatLargeNumber(totalClicks)}
                    </div>
                    <div className="dashboard-drill-down-total-label">
                        {getText('msg_eb855ad45432')}
                    </div>
                </div>
                <div className="dashboard-drill-down-table-scroll">
                    <table className="dashboard-drill-down-table">
                        <thead>
                            <tr>
                                <th className="dashboard-drill-down-filter-header">
                                    <div className="dashboard-drill-down-header-control-group">
                                        <span>
                                            {getText('msg_768e0c1c6957')}
                                        </span>
                                        <button
                                            className="dashboard-drill-down-filter-button"
                                            onClick={event =>
                                                openModal('name', event)
                                            }
                                            type="button"
                                        >
                                            {renderFilterIcon(
                                                selections.name?.length > 0,
                                            )}
                                        </button>
                                        {activeModal === 'name' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getFilterOptions(
                                                    rows,
                                                    selections,
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                modalStyle={activeModalStyle}
                                            />
                                        )}
                                    </div>
                                </th>
                                <th className="dashboard-drill-down-sortable-header">
                                    <div className="dashboard-drill-down-header-control-group">
                                        <button
                                            className="dashboard-drill-down-sortable-header-control"
                                            onClick={() =>
                                                handleSortClick('type')
                                            }
                                            type="button"
                                        >
                                            {getText('msg_3deb74565196')}
                                            {getSortArrowIcon(
                                                sortConfig.key === 'type',
                                                sortConfig.direction ===
                                                    SORT_DIRECTIONS.DESCENDING,
                                            )}
                                        </button>
                                        <button
                                            className="dashboard-drill-down-filter-button"
                                            onClick={event =>
                                                openModal('type', event)
                                            }
                                            type="button"
                                        >
                                            {renderFilterIcon(
                                                selections.type?.length > 0,
                                            )}
                                        </button>
                                        {activeModal === 'type' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getFilterOptions(
                                                    rows,
                                                    selections,
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                modalStyle={activeModalStyle}
                                            />
                                        )}
                                    </div>
                                </th>
                                <th className="dashboard-drill-down-sortable-header">
                                    <div className="dashboard-drill-down-header-control-group">
                                        <button
                                            className="dashboard-drill-down-sortable-header-control"
                                            onClick={() =>
                                                handleSortClick('location')
                                            }
                                            type="button"
                                        >
                                            {getText('msg_d219c68101f5')}
                                            {getSortArrowIcon(
                                                sortConfig.key === 'location',
                                                sortConfig.direction ===
                                                    SORT_DIRECTIONS.DESCENDING,
                                            )}
                                        </button>
                                        <button
                                            className="dashboard-drill-down-filter-button"
                                            onClick={event =>
                                                openModal('location', event)
                                            }
                                            type="button"
                                        >
                                            {renderFilterIcon(
                                                selections.location?.length > 0,
                                            )}
                                        </button>
                                        {activeModal === 'location' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getFilterOptions(
                                                    rows,
                                                    selections,
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                modalStyle={activeModalStyle}
                                            />
                                        )}
                                    </div>
                                </th>
                                <th className="dashboard-drill-down-sortable-header">
                                    <div className="dashboard-drill-down-header-control-group">
                                        <button
                                            className="dashboard-drill-down-sortable-header-control"
                                            onClick={() =>
                                                handleSortClick('page')
                                            }
                                            type="button"
                                        >
                                            {getText('msg_5b7f4b8e2a11')}
                                            {getSortArrowIcon(
                                                sortConfig.key === 'page',
                                                sortConfig.direction ===
                                                    SORT_DIRECTIONS.DESCENDING,
                                            )}
                                        </button>
                                        <button
                                            className="dashboard-drill-down-filter-button"
                                            onClick={event =>
                                                openModal('page', event, true)
                                            }
                                            type="button"
                                        >
                                            {renderFilterIcon(
                                                selections.page?.length > 0,
                                            )}
                                        </button>
                                        {activeModal === 'page' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getFilterOptions(
                                                    rows,
                                                    selections,
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                isRightColumn
                                                modalStyle={activeModalStyle}
                                            />
                                        )}
                                    </div>
                                </th>
                                <th className="dashboard-drill-down-sortable-header">
                                    <button
                                        className="dashboard-drill-down-sortable-header-control"
                                        onClick={() =>
                                            handleSortClick('clicks')
                                        }
                                        type="button"
                                    >
                                        {getText('msg_8b97ba460c72')}
                                        {getSortArrowIcon(
                                            sortConfig.key === 'clicks',
                                            sortConfig.direction ===
                                                SORT_DIRECTIONS.DESCENDING,
                                        )}
                                    </button>
                                </th>
                                <th className="dashboard-drill-down-sortable-header">
                                    <button
                                        className="dashboard-drill-down-sortable-header-control"
                                        onClick={() =>
                                            handleSortClick('uniqueClicks')
                                        }
                                        type="button"
                                    >
                                        {getText('msg_847e7f99d638')}
                                        {getSortArrowIcon(
                                            sortConfig.key === 'uniqueClicks',
                                            sortConfig.direction ===
                                                SORT_DIRECTIONS.DESCENDING,
                                        )}
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedRows.length === 0 && (
                                <tr>
                                    <td
                                        className="dashboard-drill-down-empty-state"
                                        colSpan={6}
                                    >
                                        {getText('msg_636400de23cd')}
                                    </td>
                                </tr>
                            )}
                            {paginatedRows.map(
                                function renderMostClickedRow(row) {
                                    return (
                                        <tr
                                            key={`${row.name}-${row.type}-${row.page}`}
                                        >
                                            <td>{row.name}</td>
                                            <td className="dashboard-drill-down-muted-cell">
                                                {row.type}
                                            </td>
                                            <td className="dashboard-drill-down-muted-cell">
                                                {row.location}
                                            </td>
                                            <td className="dashboard-drill-down-muted-cell">
                                                {row.page}
                                            </td>
                                            <td className="dashboard-drill-down-metric-cell">
                                                {formatLargeNumber(row.clicks)}
                                            </td>
                                            <td className="dashboard-drill-down-metric-cell-unique-clicks">
                                                {row.uniqueClicks > 0
                                                    ? formatLargeNumber(
                                                          row.uniqueClicks,
                                                      )
                                                    : '-'}
                                            </td>
                                        </tr>
                                    );
                                },
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="dashboard-drill-down-pagination">
                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
        </div>
    );
}

function getDashboardRenderer(dashboardTypeId) {
    if (dashboardTypeId === DashboardTypeEnum.MostVisitedPages) {
        return MostVisitedPagesDrillDown;
    }

    if (dashboardTypeId === DashboardTypeEnum.MostClickedContent) {
        return MostClickedContentDrillDown;
    }

    return null;
}

function DashboardDrillDownView({
    dashboardTypeId,
    dashboardData,
    endDate,
    onQueryChange,
    isLoading,
    onRowsForExportChange,
}) {
    const DashboardRenderer = getDashboardRenderer(dashboardTypeId);

    if (!DashboardRenderer) {
        return null;
    }

    return (
        <div className="dashboard-drill-down-view">
            <DashboardRenderer
                data={dashboardData}
                endDate={endDate}
                onQueryChange={onQueryChange}
                dashboardTypeId={dashboardTypeId}
                isLoading={isLoading}
                onRowsForExportChange={onRowsForExportChange}
            />
        </div>
    );
}

export default DashboardDrillDownView;
